import React from 'react';
import PropTypes from 'prop-types';
import styles from './NukeDB.module.css';
import { useAuth } from '../../../context/AuthContext';
import { SystemAPI } from '../../../utils/firebase';

const NukeDB = () => {
  const { user } = useAuth?.() || { user: null };
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [items, setItems] = React.useState([]);
  const [selected, setSelected] = React.useState(() => new Set());
  const [consent, setConsent] = React.useState(false);
  const [status, setStatus] = React.useState('');
  const [result, setResult] = React.useState(null);

  const refresh = React.useCallback(async () => {
    if (!user) return;
    setLoading(true);
    setError('');
    setStatus('Loading...');
    try {
      const data = await SystemAPI.getUserCollectionsWithCounts({ user });
      // Sort by name for stable UI
      data.sort((a, b) => a.name.localeCompare(b.name));
      setItems(data);
      setStatus('');
    } catch (e) {
      setError(e?.message || 'Failed to load collections');
      setStatus('');
    } finally {
      setLoading(false);
    }
  }, [user]);

  React.useEffect(() => { refresh(); }, [refresh]);

  const toggleAll = (checked) => {
    if (checked) {
      const s = new Set(items.map((it) => it.key));
      setSelected(s);
    } else {
      setSelected(new Set());
    }
  };

  const toggleOne = (key, checked) => {
    const s = new Set(selected);
    if (checked) s.add(key); else s.delete(key);
    setSelected(s);
  };

  const onNuke = async () => {
    if (!user) { setError('Not signed in'); return; }
    if (!consent) { setError('Please provide consent to proceed.'); return; }
    if (selected.size === 0) { setError('Select at least one collection.'); return; }
    setError('');
    setStatus('Nuking...');
    setLoading(true);
    try {
      const keys = Array.from(selected);
      const res = await SystemAPI.nukeUserCollections({ user, collectionKeys: keys, status: setStatus });
      setResult(res);
      setStatus('Completed');
      setConsent(false);
      setSelected(new Set());
      await refresh();
    } catch (e) {
      setError(e?.message || 'Nuke failed');
      setStatus('');
    } finally {
      setLoading(false);
    }
  };

  const noneSelected = selected.size === 0;

  return (
    <div className={styles.NukeDB}>
      <div className={styles.headerRow}>
        <div className={styles.title}>Danger Zone 🔥🌋 — Nuke Your Data</div>
        <button type="button" className={styles.refreshBtn} onClick={refresh} disabled={loading}>Refresh</button>
      </div>

      {error ? <div className={styles.error}>{error}</div> : null}
      {status ? <div className={styles.status}>{status}</div> : null}

      <div className={styles.collectionsCard}>
        <div className={styles.selectAllRow}>
          <label className={styles.checkboxLabel}>
            <input type="checkbox" onChange={(e) => toggleAll(e.target.checked)} checked={selected.size === items.length && items.length > 0} />
            <span>Select all</span>
          </label>
        </div>

        <div className={styles.listHead}>
          <div className={styles.colCheck}></div>
          <div className={styles.colName}>Collection</div>
          <div className={styles.colCount}>Docs</div>
        </div>

        <div className={styles.listBody}>
          {items.map((it) => (
            <div key={it.key} className={styles.listRow}>
              <div className={styles.colCheck}>
                <input
                  type="checkbox"
                  checked={selected.has(it.key)}
                  onChange={(e) => toggleOne(it.key, e.target.checked)}
                />
              </div>
              <div className={styles.colName}>{it.name} <span className={styles.keyBadge}>{it.key}</span></div>
              <div className={styles.colCount}>{it.count}</div>
            </div>
          ))}
          {items.length === 0 && (
            <div className={styles.empty}>No user collections found.</div>
          )}
        </div>
      </div>

      <div className={styles.consentRow}>
        <label className={styles.checkboxLabel}>
          <input type="checkbox" checked={!!consent} onChange={(e) => setConsent(e.target.checked)} />
          <span>⚠️ I understand this action is irreversible and will delete my selected data.</span>
        </label>
      </div>

      <div className={styles.actionsRow}>
        <button
          type="button"
          className={`${styles.nukeBtn} ${(!consent || noneSelected || loading) ? styles.disabled : ''}`}
          onClick={onNuke}
          disabled={!consent || noneSelected || loading}
        >
          NUKE SELECTED 💣
        </button>
      </div>

      {Array.isArray(result) && result.length > 0 && (
        <div className={styles.resultCard}>
          <div className={styles.resultTitle}>Results</div>
          <ul className={styles.resultList}>
            {result.map((r) => (
              <li key={r.key}>
                <span className={styles.resultName}>{r.name}</span>: deleted {r.deleted}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

NukeDB.propTypes = {};

NukeDB.defaultProps = {};

export default NukeDB;
